import logo from 'assets/header-prevencor-xx.png';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import fetchImageBase64 from 'utils/helpers';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

function sectionTitle(text) {
  return {
    text,
    bold: true,
    decoration: 'underline',
    margin: [0, 8, 0, 3],
  };
}

function buildAnatomiaLines(items = [], { mostraMedida = false } = {}) {
  if (!Array.isArray(items) || !items.length) return [];

  return items
    .map((item) => {
      const partes = [String(item?.ANATOMIA || '').trim()];
      if (mostraMedida && item?.CODANATOMIA === 44 && item?.MEDIDA != null) {
        partes.push(`${item.MEDIDA} mm`);
      }
      if (item?.VELOCIDADE) partes.push(String(item.VELOCIDADE));
      if (item?.INTENSIDADE) partes.push(String(item.INTENSIDADE));
      const text = partes.filter(Boolean).join(' ').trim();
      return text ? { text, margin: [10, 0, 0, 1] } : null;
    })
    .filter(Boolean);
}

export async function generatePDFCarotida({ carotida }) {
  const scan = carotida?.carotida?.[0];
  if (!carotida || !scan) return;

  const logoBase64 = await fetchImageBase64(logo);
  const hashAssinatura = md5(JSON.stringify(carotida));

  const content = [
    {
      text: 'Laudo de Scan de Carotidas',
      bold: true,
      decoration: 'underline',
      alignment: 'center',
      margin: [0, 0, 0, 10],
    },
    {
      text: [{ text: 'Paciente: ', bold: true }, `${scan.CODPAC || '-'} - ${scan.NOMEPAC || '-'}`],
      margin: [0, 0, 0, 1],
    },
    {
      columns: [
        {
          text: [
            { text: 'Sexo: ', bold: true },
            String(scan.SEXOPAC || '-'),
            { text: '   Idade: ', bold: true },
            `${scan.idadePAC ?? '-'} anos`,
          ],
          width: '*',
        },
        {
          text: [{ text: 'Data do Exame: ', bold: true }, String(scan.dataScanFmt || '-')],
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      columns: [
        {
          text: [{ text: 'Medico que Realizou: ', bold: true }, `Dr.(a): ${scan.Realizou || '-'}`],
          width: '*',
        },
        {
          text: String(scan.CODSCAN || ''),
          bold: true,
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Medico que Solicitou: ', bold: true }, `Dr.(a): ${scan.Solicitante || '-'}`],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Convenio: ', bold: true }, String(scan.SIGLACONV || '-')],
      margin: [0, 1, 0, 6],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
      margin: [0, 0, 0, 6],
    },

    sectionTitle('Sistema Carotideo Direito'),
    sectionTitle('Carotida Comum Direita'),
    ...buildAnatomiaLines(carotida.ccd, { mostraMedida: true }),
    sectionTitle('Carotida Interna Direita'),
    ...buildAnatomiaLines(carotida.cid),
    sectionTitle('Carotida Externa Direita'),
    ...buildAnatomiaLines(carotida.ced),

    sectionTitle('Sistema Carotideo Esquerdo'),
    sectionTitle('Carotida Comum Esquerda'),
    ...buildAnatomiaLines(carotida.cce, { mostraMedida: true }),
    sectionTitle('Carotida Interna Esquerda'),
    ...buildAnatomiaLines(carotida.cie),
    sectionTitle('Carotida Externa Esquerda'),
    ...buildAnatomiaLines(carotida.cee),

    sectionTitle('Arterias Vertebrais'),
    sectionTitle('Arteria Vertebral Direita'),
    ...buildAnatomiaLines(carotida.avd),
    sectionTitle('Arteria Vertebral Esquerda'),
    ...buildAnatomiaLines(carotida.ave),

    ...(scan.OBSERVACOES
      ? [sectionTitle('Observacoes'), { text: String(scan.OBSERVACOES), margin: [10, 0, 0, 1] }]
      : []),

    {
      text: 'Conferido e assinado eletronicamente por:',
      italics: true,
      alignment: 'center',
      margin: [0, 20, 0, 3],
    },
    {
      text: `Dr(a).: ${scan.Realizou || '-'}`,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 3],
    },
    {
      text: `Assinatura Digital: ${hashAssinatura}`,
      bold: true,
      italics: true,
      alignment: 'center',
      fontSize: 9,
      margin: [0, 0, 0, 12],
    },
  ].filter(Boolean);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 90, 40, 68],
    header: function () {
      return {
        stack: [
          {
            image: logoBase64,
            width: 460,
            alignment: 'center',
            margin: [0, 6, 0, 2],
          },
          {
            text: String(scan.CODSCAN || ''),
            bold: true,
            alignment: 'right',
            fontSize: 10,
            margin: [40, 0, 40, 0],
          },
        ],
      };
    },
    footer: function (currentPage, pageCount) {
      return [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
          margin: [40, 2, 40, 0],
        },
        {
          text: 'Rua Claudio Manuel da Costa, 56 - Fone (14) 3402-2700/Fax: (14) 3402-2705 - Jd Maria Isabel - CEP 17.515-460 - Marilia/SP',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 2, 40, 0],
        },
        {
          text: 'prevencor@unimedmarilia.com.br',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 0, 40, 0],
        },
        {
          text: `Pag: ${currentPage} de ${pageCount}`,
          alignment: 'center',
          fontSize: 8,
          bold: true,
          margin: [40, 1, 40, 0],
        },
      ];
    },
    content,
    defaultStyle: { fontSize: 10 },
  };

  pdfMake.createPdf(docDefinition).open();
}
